/**
 * useCvSync — Synchronisation automatique des CVs avec Supabase.
 *
 * Comportement :
 * - Au login : charge les CVs depuis le serveur.
 *   Résolution de conflits "last modified wins" :
 *   • Serveur vide → push local vers serveur
 *   • Serveur plus récent que local → merge serveur dans local state
 *   • Local plus récent → push local vers serveur
 * - Sur changement CV (debounce 3s) : push snapshot complet vers serveur.
 *
 * Ce hook est offline-first : si le serveur est inaccessible, le local persiste.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, apiFetchGet } from '../lib/api-fetch';
import {
  type UseCvStateReturn,
  type ServerCvVersion,
  CV_STATE_UPDATED_AT_KEY,
  mergeFromServer,
} from './useCvState';

export type SyncStatus = 'idle' | 'syncing' | 'error';

export function useCvSync(cvState: UseCvStateReturn): { syncStatus: SyncStatus } {
  const { session } = useAuth();
  const syncStatusRef = useRef<SyncStatus>('idle');
  const hasSyncedOnLoginRef = useRef(false);
  const pushDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Évite de re-pousser vers le serveur les données qu'on vient de charger
  // depuis le serveur (mergeFromServer déclenche des changements d'état qui
  // sinon déclencheraient le debounce push inutilement).
  const justMergedFromServerRef = useRef(false);

  // Capturé UNE SEULE FOIS au moment du premier render, AVANT que les effets
  // de persistance de useCvState ne touchent cv_state_updated_at.
  // Sur un appareil neuf (localStorage vide), ce sera '' → le serveur gagne.
  // Sur un appareil avec données, ce sera le vrai timestamp de la dernière
  // modification → comparaison fiable avec le serveur.
  const [localTimestampAtMount] = useState<string>(() => {
    try { return localStorage.getItem(CV_STATE_UPDATED_AT_KEY) ?? ''; } catch { return ''; }
  });

  // ── Helper : push snapshot complet vers le serveur ───────────────────────
  const pushToServer = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      await apiFetch('/api/user/cv-sync', {
        snapshot: {
          allTemplatesData: cvState.allTemplatesData,
          templateMeta: cvState.templateMeta,
          commonInfo: cvState.commonInfo,
          fieldVisibility: cvState.fieldVisibility,
          activeTemplate: cvState.activeTemplate,
        },
      });
    } catch (e) {
      console.error('[useCvSync] pushToServer error:', e);
    }
  }, [session, cvState.allTemplatesData, cvState.templateMeta, cvState.commonInfo, cvState.fieldVisibility, cvState.activeTemplate]);

  // ── Au login : charger depuis le serveur et résoudre les conflits ─────────
  useEffect(() => {
    if (!session?.access_token) {
      // Reset le flag au logout pour que le prochain login re-sync
      hasSyncedOnLoginRef.current = false;
      return;
    }
    if (hasSyncedOnLoginRef.current) return;
    hasSyncedOnLoginRef.current = true;

    async function syncOnLogin() {
      syncStatusRef.current = 'syncing';
      try {
        const { json } = await apiFetchGet<{ cvVersions: ServerCvVersion[] }>(
          '/api/user/cv-sync',
        );

        const serverVersions = json.cvVersions ?? [];

        if (serverVersions.length === 0) {
          // Serveur vide → push le local (premier login ou compte vide)
          await pushToServer();
          syncStatusRef.current = 'idle';
          return;
        }

        // Trouver le timestamp serveur le plus récent
        const serverUpdatedAt = serverVersions
          .map((v) => v.updated_at)
          .filter(Boolean)
          .sort()
          .reverse()[0] ?? '';

        // Timestamp capturé au mount (avant que les effets de persistance
        // n'écrasent la valeur avec "maintenant" sur un appareil neuf).
        const localUpdatedAt = localTimestampAtMount;

        if (!localUpdatedAt || serverUpdatedAt > localUpdatedAt) {
          // Serveur plus récent → charger dans le state local
          justMergedFromServerRef.current = true;
          mergeFromServer(serverVersions, {
            setActiveTemplate: cvState.setActiveTemplate,
            setTemplateMeta: cvState.setTemplateMeta,
            setAllTemplatesData: cvState.setAllTemplatesData,
            setCommonInfo: cvState.setCommonInfo,
            setFieldVisibility: cvState.setFieldVisibility,
          }, serverUpdatedAt);
        } else {
          // Local plus récent → push vers serveur
          await pushToServer();
        }

        syncStatusRef.current = 'idle';
      } catch (e) {
        console.error('[useCvSync] syncOnLogin error:', e);
        syncStatusRef.current = 'error';
      }
    }

    void syncOnLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]); // Re-run uniquement au changement de session (login/logout)

  // ── Sur changement CV : push debounced vers le serveur ───────────────────
  useEffect(() => {
    if (!session?.access_token) return;
    // Si on vient de charger depuis le serveur (mergeFromServer), ne pas re-pousser
    // les données qu'on vient juste de recevoir — inutile et ça bumpe updated_at.
    if (justMergedFromServerRef.current) {
      justMergedFromServerRef.current = false;
      return;
    }
    if (pushDebounceRef.current) clearTimeout(pushDebounceRef.current);
    pushDebounceRef.current = setTimeout(() => {
      void pushToServer();
    }, 3000); // 3 secondes de debounce

    return () => {
      if (pushDebounceRef.current) clearTimeout(pushDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cvState.allTemplatesData,
    cvState.commonInfo,
    cvState.templateMeta,
    cvState.fieldVisibility,
    // session.access_token intentionnellement exclu : on ne veut pas re-push au login
  ]);

  return { syncStatus: syncStatusRef.current };
}
